import React, { useState, useEffect, useMemo, useRef } from 'react'; // Добавляем useMemo и useRef
import { useParams, useLocation } from 'react-router-dom';
import { ApiService } from '../services/api';
import { ApplicationDetails, SearchedProductName, WebSocketMessage } from '../types/models';
import SearchedProductCard from '../components/SearchedProductCard';
import ProductVariantsList from '../components/ProductVariantsList';
import BrandReselectionModal from '../components/BrandReselectionModal';
import { useAppContext } from '../context/AppContext';
import { webSocketService } from '../services/websocket';
import  {Tour, TourProvider, useTour } from '@reactour/tour'; // Импортируем Tour

const ApplicationPage: React.FC = () => {
  const { appId } = useParams<{ appId: string }>();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const clientAppNumber = queryParams.get('clientAppNumber');
  const [application, setApplication] = useState<ApplicationDetails | null>(null);
  const [selectedSpnId, setSelectedSpnId] = useState<number | null>(null);
  const [selectedSpn, setSelectedSpn] = useState<SearchedProductName | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [variantsRefreshTrigger, setVariantsRefreshTrigger] = useState<number>(0);
  const [groupedCategories, setGroupedCategories] = useState<Map<number, { level3Category: { id: number, name: string | null }, level2Categories: { id: number, name: string | null }[] }>>(new Map());
  const [selectedLevel3CategoryId, setSelectedLevel3CategoryId] = useState<number | null>(null);
  const [selectedLevel2CategoryId, setSelectedLevel2CategoryId] = useState<number | null>(null);
  const [availableBrands, setAvailableBrands] = useState<string[]>([]);
  const [selectedBrand, setSelectedBrand] = useState<string | null>(null);
  const [isFilterPanelHovered, setIsFilterPanelHovered] = useState(false); // Новое состояние для ховера панели фильтров
  const [isBrandReselectionModalOpen, setIsBrandReselectionModalOpen] = useState(false); // Состояние для модального окна переподбора

  // Состояния для онбординга React Tour
  const { setIsOpen } = useTour();
  const [currentStep, setCurrentStep] = useState(0);

  const prevL3CatIdForBrandEffectRef = useRef(selectedLevel3CategoryId);
  const prevL2CatIdForBrandEffectRef = useRef(selectedLevel2CategoryId);
  const prevSelectedSpnIdForBrandEffectRef = useRef(selectedSpnId);
  const initialSpnSelectionDoneRef = useRef(false);

  // Get values from the AppContext
  const { userId, searchingSpnIds, addSearchingSpnId, removeSearchingSpnId, setSearchEventHandlers } = useAppContext(); // Добавили setSearchEventHandlers

  useEffect(() => {
    // Fetch application data when component mounts or appId changes
    if (appId) {
      fetchApplicationData();
    }
  }, [appId]);

  // useEffect для инициализации онбординга React Tour
  useEffect(() => {
    const hasCompletedOnboarding = globalThis.window === undefined ? null : localStorage.getItem('hasCompletedApplicationPageOnboarding');
    //alert(isTourOpen)
     if (!hasCompletedOnboarding && application && selectedSpnId !== null) {
      setIsOpen(true);
    }
    
  }, [application, selectedSpnId]);

  // Group categories when application data changes
  useEffect(() => {
    if (application) {
      const categoriesMap = new Map<number, { level3Category: { id: number, name: string | null }, level2Categories: { id: number, name: string | null }[] }>();

      application.products.forEach(spn => {
        if (spn.category && spn.category.level3_category) {
          const level3Id = spn.category.level3_category.id;
          const level2Id = spn.category.id;

          if (!categoriesMap.has(level3Id)) {
            categoriesMap.set(level3Id, {
              level3Category: spn.category.level3_category,
              level2Categories: []
            });
          }

          const level3Entry = categoriesMap.get(level3Id)!;
          // Check if level2 category is already added to avoid duplicates
          if (!level3Entry.level2Categories.some(cat => cat.id === level2Id)) {
            level3Entry.level2Categories.push({
              id: level2Id,
              name: spn.category.name
            });
          }
        }
      });
      setGroupedCategories(categoriesMap);
    }
  }, [application]);

  // --- useEffect для ЗАГРУЗКИ брендов ТОЛЬКО при смене КАТЕГОРИИ ---
  useEffect(() => {
    console.log("Brand loading useEffect. Current L3:", selectedLevel3CategoryId, "Prev L3:", prevL3CatIdForBrandEffectRef.current, "Current L2:", selectedLevel2CategoryId, "Prev L2:", prevL2CatIdForBrandEffectRef.current);

    const l3CategoryActuallyChanged = prevL3CatIdForBrandEffectRef.current !== selectedLevel3CategoryId;
    const l2CategoryActuallyChanged = prevL2CatIdForBrandEffectRef.current !== selectedLevel2CategoryId;
    const spnSelectionActuallyChanged = prevSelectedSpnIdForBrandEffectRef.current !== selectedSpnId;

    if (l3CategoryActuallyChanged || l2CategoryActuallyChanged || spnSelectionActuallyChanged) {
      console.log("Category or SPN change detected in brand effect. Resetting brand and available brands.");
      setSelectedBrand(null);
      setAvailableBrands([]); // Очищаем перед загрузкой новых, т.к. категория или SPN сменились
    }
  
    let spnsToFetchBrandsFor: SearchedProductName[] = [];

    if (selectedSpn && application) {
      // Если выбран конкретный SPN, получаем бренды только для него
      spnsToFetchBrandsFor = [selectedSpn]; // Используем selectedSpn напрямую
      console.log('Fetching brands for selected SPN:', selectedSpn.id);
    } else if ((selectedLevel3CategoryId || selectedLevel2CategoryId) && application) {
      // Если выбраны категории (и не выбран конкретный SPN), получаем бренды для SPN в этих категориях
      console.log('Fetching brands for L3:', selectedLevel3CategoryId, "L2:", selectedLevel2CategoryId);
      spnsToFetchBrandsFor = application.products.filter(spn => {
         if (spn.category) { // Добавлена проверка на существование spn.category
             if (selectedLevel2CategoryId) return spn.category.id === selectedLevel2CategoryId;
             if (selectedLevel3CategoryId) return spn.category.level3_category?.id === selectedLevel3CategoryId;
         }
         return false;
      });
    }

    if (spnsToFetchBrandsFor.length > 0) {
      const spnIds = spnsToFetchBrandsFor.map(spn => spn.id);
      ApiService.getBrandsForSpns(spnIds)
        .then(brands => {
          const uniqueBrands = Array.from(new Set(brands)).sort();
          setAvailableBrands(uniqueBrands);
          console.log('Available brands loaded:', uniqueBrands);
          if (selectedBrand && !uniqueBrands.includes(selectedBrand)) {
            console.log(`Previously selected brand "${selectedBrand}" is no longer available. Resetting.`);
            setSelectedBrand(null);
          }
        })
        .catch(err => {
          console.error('Failed to fetch brands:', err);
          setAvailableBrands([]);
          setSelectedBrand(null);
        });
    } else {
       console.log("No SPNs found for the selected criteria. Clearing available brands.");
       setAvailableBrands([]);
       if (selectedBrand) {
          setSelectedBrand(null);
       }
    }

    // Обновляем ref значения в конце эффекта для следующего запуска
    prevL3CatIdForBrandEffectRef.current = selectedLevel3CategoryId;
    prevL2CatIdForBrandEffectRef.current = selectedLevel2CategoryId;
    prevSelectedSpnIdForBrandEffectRef.current = selectedSpnId;

  }, [selectedLevel3CategoryId, selectedLevel2CategoryId, selectedSpn, application, selectedBrand, availableBrands.length]); // Добавлены selectedBrand и availableBrands.length для корректной работы логики сброса/очистки

  // --- useMemo для filteredSpns ---
  // Убираем фильтрацию по бренду из этого useMemo, т.к. фильтруем варианты, а не SPN
  const filteredSpns = useMemo(() => {
    if (!application) {
      return [];
    }
    return application.products.filter(spn => {
      // Только фильтрация по категории
      if (!selectedLevel3CategoryId && !selectedLevel2CategoryId) return true;
      if (selectedLevel2CategoryId) return spn.category?.id === selectedLevel2CategoryId;
      if (selectedLevel3CategoryId) return spn.category?.level3_category?.id === selectedLevel3CategoryId;
      return true;
    });
  }, [application, selectedLevel3CategoryId, selectedLevel2CategoryId]); // Убрали selectedBrand из зависимостей

  // --- useMemo для группировки SPN по section ---
  const groupedBySection = useMemo(() => {
    if (!application || !application.products) {
      return { hasSection: false, groups: [], ungrouped: [] };
    }

    // Проверяем, есть ли хоть один SPN с заполненным section
    const hasSectionData = application.products.some(spn => spn.section && spn.section.trim() !== '');
    
    if (!hasSectionData) {
      // Если нет секций, возвращаем все как ungrouped
      return { hasSection: false, groups: [], ungrouped: filteredSpns };
    }

    // Группируем по section
    const sectionMap = new Map<string, SearchedProductName[]>();
    const ungroupedSpns: SearchedProductName[] = [];

    filteredSpns.forEach(spn => {
      const section = spn.section?.trim();
      if (section) {
        if (!sectionMap.has(section)) {
          sectionMap.set(section, []);
        }
        sectionMap.get(section)!.push(spn);
      } else {
        ungroupedSpns.push(spn);
      }
    });

    // Преобразуем в массив и сортируем группы по минимальному ID внутри каждой группы
    const groups = Array.from(sectionMap.entries())
      .map(([section, spns]) => {
        // Сортируем SPN внутри группы по ID для сохранения порядка из документации
        const sortedSpns = [...spns].sort((a, b) => a.id - b.id);
        
        // Минимальный ID в группе для сортировки групп
        const minId = sortedSpns.length > 0 ? sortedSpns[0].id : Infinity;
        
        // Вычисляем общую сумму для секции
        const totalPrice = sortedSpns.reduce((sum, spn) => {
          if (spn.selected_product_code && spn.selected_product_details) {
            const price = spn.selected_product_details.price;
            const quantity = spn.quantity !== undefined && spn.quantity !== null ? spn.quantity : 1;
            if (price !== undefined) {
              return sum + (price * quantity);
            }
          }
          return sum;
        }, 0);

        return {
          section,
          spns: sortedSpns,
          totalPrice,
          count: sortedSpns.length,
          minId
        };
      })
      .sort((a, b) => a.minId - b.minId); // Сортируем группы по минимальному ID

    return { hasSection: true, groups, ungrouped: ungroupedSpns };
  }, [application, filteredSpns]);

  // Состояние для раскрытия/закрытия секций
  const [expandedSections, setExpandedSections] = React.useState<Set<string>>(new Set());

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  // --- useMemo для вычисления общей суммы выбранных вариантов ---
  const totalSelectedPrice = useMemo(() => {
    if (!application || !application.products) {
      return 0;
    }

    let total = 0;
    application.products.forEach(spn => {
      // Проверяем, выбран ли вариант продукта и есть ли детали выбранного продукта
      if (spn.selected_product_code && spn.selected_product_details) {
        const price = spn.selected_product_details.price;
        const quantity = spn.quantity !== undefined && spn.quantity !== null ? spn.quantity : 1; // Используем quantity из SPN

        // Если цена существует
        if (price !== undefined) {
          total += price * quantity;
        }
      }
    });

    return total;
  }, [application]); // Пересчитываем при изменении application

  const { articlesFilledPercentage, featuresFilledPercentage, notFilledPercentage } = useMemo(() => {
    if (!application || !application.products || application.products.length === 0) {
      return { articlesFilledPercentage: 0, featuresFilledPercentage: 0, notFilledPercentage: 0 };
    }

    const totalSpnCount = application.products.length;
    let articlesCount = 0;
    let featuresOnlyCount = 0;
    let notFilledCount = 0;

    application.products.forEach(spn => {
      const hasArticle = spn.article && spn.article.trim() !== '';
      const hasFeatures = spn.etim_features && spn.etim_features.length > 0;

      if (hasArticle) {
        articlesCount++;
      } else if (hasFeatures) {
        featuresOnlyCount++;
      } else {
        notFilledCount++;
      }
    });

    return {
      articlesFilledPercentage: (articlesCount / totalSpnCount) * 100,
      featuresFilledPercentage: (featuresOnlyCount / totalSpnCount) * 100,
      notFilledPercentage: (notFilledCount / totalSpnCount) * 100,
    };
  }, [application]);

  const completionPercentage = useMemo(() => {
    if (!application || !application.products || application.products.length === 0) {
      return 0;
    }
    const totalSpnCount = application.products.length;
    const completedSpnCount = application.products.filter(spn => spn.selected_product_code).length;
    return (completedSpnCount / totalSpnCount) * 100;
  }, [application]);

  // Update selectedSpn when selectedSpnId changes or application data refreshes
  useEffect(() => {
    if (selectedSpnId && application) {
      // Ищем в исходном списке продуктов, а не в отфильтрованном
      const spn = application.products.find(p => p.id === selectedSpnId) || null;
      setSelectedSpn(spn);
    } else {
      setSelectedSpn(null);
    }
  }, [selectedSpnId, application]); // Зависимость от application важна

  // Оборачиваем функции в useCallback и определяем их перед useEffect, который их использует
  const handleSearchComplete = React.useCallback(async (spnId: number) => {
    console.log(`ApplicationPage: handleSearchComplete called for spn_id: ${spnId}`);
    try {
      const updatedSpn = await ApiService.getSPN(spnId);
      console.log(`ApplicationPage: Fetched updated SPN for spn_id: ${spnId}, is_searching should be false. Data:`, updatedSpn);
      setApplication(prev => {
        if (prev) {
          console.log(`ApplicationPage: Updating application state for spn_id: ${spnId}. Setting is_searching to false.`);
          const searchingSpn = prev.products.find(spn => spn.id === spnId);
          if (searchingSpn) {
            // Copy all properties from updatedSpn to searchingSpn except orderNumber
            const orderNumber = searchingSpn.orderNumber; // Preserve the original orderNumber
            Object.assign(searchingSpn, updatedSpn);
            searchingSpn.orderNumber = orderNumber; // Restore the original orderNumber
            searchingSpn.is_searching = false; // Ensure is_searching is false
          }
          return { ...prev, products: [...prev.products] };
        }
        return null;
      });
      if (selectedSpnId === spnId) {
        console.log(`ApplicationPage: spn_id: ${spnId} is currently selected. Updating selectedSpn and triggering variants refresh.`);
        setSelectedSpn(updatedSpn);
        setVariantsRefreshTrigger(Date.now());
      }
    } catch (error) {
      console.error('Failed to update SPN after search completion:', error);
    }
  }, [selectedSpnId]);

  const handleSearchError = React.useCallback((spnId: number, errorMessage?: string) => {
    setApplication(prev =>
      prev ? {
        ...prev,
        products: prev.products.map(spn => spn.id === spnId ? { ...spn, is_searching: false, searchError: errorMessage } : spn)
      } : null
    );
  }, []); 

  const memoizedSearchEventHandlers = React.useMemo(() => ({
    onSearchComplete: handleSearchComplete,
    onSearchError: handleSearchError,
  }), [handleSearchComplete, handleSearchError]);

  useEffect(() => {
    if (setSearchEventHandlers) {
      setSearchEventHandlers(memoizedSearchEventHandlers);
    }
    return () => {
      if (setSearchEventHandlers) {
        setSearchEventHandlers(null);
      }
    };
  }, [setSearchEventHandlers, memoizedSearchEventHandlers]);

  // Эффект для автоматического выбора первой позиции SPN при загрузке заявки
  useEffect(() => {
    if (application && !selectedSpnId && application.products.length > 0 && !initialSpnSelectionDoneRef.current) {
      // Выбираем первую позицию SPН, если она еще не выбрана
      const firstSpn = application.products.sort((a, b) => (a.orderNumber || 0) - (b.orderNumber || 0))[0];
      if (firstSpn) {
        setSelectedSpnId(firstSpn.id);
        initialSpnSelectionDoneRef.current = true; // Устанавливаем флаг, что первоначальный выбор выполнен
      }
    }
  }, [application, selectedSpnId]);

  const fetchApplicationData = async () => {
    if (!appId) return;

    setIsLoading(true);
    setError(null);

    try {
      const applicationData = await ApiService.getApplication(parseInt(appId));
      setApplication(applicationData);
    } catch (err: any) {
      if (err.response) {
        if (err.response.status === 403) {
          setError("У вас нет прав доступа к этой заявке.");
        } else if (err.response.status === 404) {
          setError("Заявка не найдена.");
        } else if (err.response.status === 401) {
          setError("Сессия истекла или недействительна. Пожалуйста, войдите снова.");
          // logout(); // Раскомментируйте, если используете logout из AppContext
        } else {
          setError(`Ошибка загрузки данных: ${err.response.data.detail || err.message}`);
        }
      } else {
        setError(`Ошибка сети или неизвестная ошибка: ${err.message}`);
      }
      console.error("Failed to fetch application data:", err);
    } finally {
      setIsLoading(false);
    }
  };
  
  
const handleSpnUpdate = (updatedSpn: SearchedProductName) => {
  console.log("handleSpnUpdate called with:", updatedSpn);
  setApplication(prevApp => {
    if (!prevApp) return null;

    // Определяем sourceSpnId для распространения изменений
    // Если updatedSpn имеет parent_spn_id, то источник - его родитель.
    // Иначе, источник - сам updatedSpn (если он является родительским).
    const sourceSpnId = updatedSpn.parent_spn_id !== null ? updatedSpn.parent_spn_id : updatedSpn.id;

    const updatedProducts = prevApp.products.map(spn => {
      // Если это сам обновленный SPN, просто возвращаем его
      if (spn.id === updatedSpn.id) {
        return updatedSpn;
      }
      
      // Если это родительская позиция для обновленного дочернего SPN
      // ИЛИ если это дочерняя позиция, связанная с обновленным родительским SPN
      // ИЛИ если это другая дочерняя позиция, связанная с тем же родителем, что и обновленный дочерний SPN
      if (
        (updatedSpn.parent_spn_id !== null && spn.id === updatedSpn.parent_spn_id) || // spn - родитель для updatedSpn
        (spn.parent_spn_id !== null && spn.parent_spn_id === sourceSpnId) // spn - дочерний для sourceSpnId
      ) {
        
        // Копируем ключевые поля из updatedSpn
        return {
          ...spn,
          selected_product_code: updatedSpn.selected_product_code,
          category: updatedSpn.category, // Обновляем весь объект category
          etim_features: JSON.parse(JSON.stringify(updatedSpn.etim_features || [])), // Глубокая копия
          article: updatedSpn.article,
          name: updatedSpn.name,
          selected_product_details: updatedSpn.selected_product_details
            ? JSON.parse(JSON.stringify(updatedSpn.selected_product_details))
            : null,
          vendor_match: updatedSpn.vendor_match
            ? JSON.parse(JSON.stringify(updatedSpn.vendor_match))
            : null,
        };
      }
      return spn; // Возвращаем неизмененный SPN
    });
    //alert(updatedProducts.map(spn => `SPN ID: ${spn.id}, Name: ${spn.name}, Selected Product Code: ${spn.selected_product_code}`).join('\n'));
    return {
      ...prevApp,
      products: updatedProducts
    };
  });

  if (selectedSpnId === updatedSpn.id) {
    setSelectedSpn(updatedSpn);
  }
};

const handleFeaturesSaved = (updatedSpn: SearchedProductName) => {
  console.log("handleFeaturesSaved called with:", updatedSpn); 

  setApplication(prevApp => {
    if (!prevApp) return null;

    const updatedProducts = prevApp.products.map(spn =>
      spn.id === updatedSpn.id ? updatedSpn : spn
    );

    return {
      ...prevApp,
      products: updatedProducts
    };
  });

  if (selectedSpnId === updatedSpn.id) {
    setSelectedSpn(updatedSpn);
    setVariantsRefreshTrigger(Date.now());
  }
};
const handleDownloadExcel = async () => {
    if (!appId) return;

    try {
      const response = await fetch(`/api/applications/${appId}/export-excel`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const tempLink = document.createElement('a');
      tempLink.href = url;
      tempLink.setAttribute('download', `application_${appId}_export.xlsx`);
      document.body.appendChild(tempLink);
      tempLink.click();
      document.body.removeChild(tempLink);
      URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Failed to download Excel file:', error);
    }
  };


  const handleStartSearch = async (spnId: number) => {
    try {
      addSearchingSpnId(spnId);
      // await ApiService.repeatSearch(spnId, userId); // userId может быть null
    } catch (err) {
      console.error('Failed to start search:', err);
      removeSearchingSpnId(spnId);
    }
  };

  const handleViewVariants = (spnId: number) => {
    setSelectedSpnId(spnId === selectedSpnId ? null : spnId);
  };

  const updateSpnAfterSearch = async (spnId: number) => {
    try {
      const updatedSpn = await ApiService.getSPN(spnId);
      
      setApplication(prev => 
        prev ? {
          ...prev,
          products: prev.products.map(spn => spn.id === spnId ? { ...updatedSpn, is_searching: false } : spn)
        } : null
      );
      
      if (selectedSpnId === spnId) {
        setSelectedSpn(updatedSpn);
      }
    } catch (error) {
      console.error('Failed to update SPN after search:', error);
    }
  };

  const handleBrandClick = (brand: string | null) => {
    console.log("Setting selected brand:", brand); 
    setSelectedBrand(brand);
  };

  // Получение списка категорий 3 уровня для переподбора
  const getLevel3CategoriesForReselection = () => {
    const categories: Array<{ id: number; name: string }> = [];
    groupedCategories.forEach((group, level3Id) => {
      if (group.level3Category.name) {
        categories.push({
          id: level3Id,
          name: group.level3Category.name
        });
      }
    });
    return categories;
  };

  const handleReselectionComplete = () => {
    // Обновляем данные заявки после завершения переподбора
    if (appId) {
      fetchApplicationData();
    }
    // Сбрасываем выбранный SPN чтобы обновить список вариантов
    setVariantsRefreshTrigger(prev => prev + 1);
  };

  if (isLoading && !application) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <p>{error}</p>
          <button 
            onClick={fetchApplicationData}
            className="mt-2 px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Повторить попытку
          </button>
        </div>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded">
          <p>Заявка не найдена.</p>
        </div>
      </div>
    );
  }

  const handleLevel3CategoryClick = (categoryId: number | null) => {
    setSelectedLevel3CategoryId(categoryId);
    setSelectedLevel2CategoryId(null); 
    setSelectedBrand(null); 
  };

  const handleLevel2CategoryClick = (categoryId: number | null) => {
    setSelectedLevel2CategoryId(categoryId);
    setSelectedBrand(null); 
  };


  return (
    <div className="px-2 py-2">
      <div className="bg-white shadow-sm rounded-lg p-2 mb-2">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-xl font-bold">
            Заявка {clientAppNumber ? `#${clientAppNumber}` : ''} от {new Date(application.application_date).toLocaleDateString()}
          </h1>
          <div className="flex items-center w-2/3">
            {totalSelectedPrice > 0 && (
              <span className='text-sm font-medium text-gray-700 dark:text-gray-300 ml-2 pr-2' title='Общая сумма выбранных позиций'>
                Общая сумма заявки: {totalSelectedPrice.toFixed(2)} ₽
              </span>
            )}
            <span className='text-sm font-medium text-gray-700 dark:text-gray-300 ml-2 pr-2' title='Процент подобранных позиций к общему числу искомых позиций'>Выполнение: </span>
            <div className="w-full bg-gray-200 rounded-full h-5 dark:bg-gray-700">
              <div
                className="bg-blue-600 h-5 rounded-full transition-all duration-300 ease-in-out"
                style={{ width: `${completionPercentage}%` }}
              ></div>
            </div>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 ml-2">
              {`${Math.round(completionPercentage)}%`}
            </span>
            <button
              onClick={handleDownloadExcel}
              className="ml-4 px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors duration-200"
            >
              Выгрузить в Excel
            </button>
            <button
              onClick={() => setIsBrandReselectionModalOpen(true)}
              className="ml-2 px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 transition-colors duration-200"
              title="Переподбор артикулов по брендам"
            >
              Переподбор Бренда
            </button>
          </div>
        </div>
        {application.comment && (
          <p className="text-gray-600 mb-4">{application.comment}</p>
        )}
      </div>

      <div
        className="relative mb-2 shadow-sm"
        onMouseEnter={() => setIsFilterPanelHovered(true)}
        onMouseLeave={() => setIsFilterPanelHovered(false)}
        data-testid="general-filters-panel"
      >
        
        <div className={`grid grid-cols-4 gap-2 p-1 bg-white rounded-lg border border-gray-200 transition-opacity duration-100 ${isFilterPanelHovered ? 'opacity-0 invisible' : 'opacity-100 visible'}`}>
          <div className="flex flex-wrap items-center gap-1 overflow-hidden">
            <h1 className="px-2 py-0.5 rounded-full text-sm font-medium">Общие фильтры:</h1>
          </div>
          <div className="flex flex-wrap items-center gap-1 overflow-hidden">
            <span className="px-2 py-0.5 text-xs font-medium">Категория:</span>
            <span
              className={`px-2 py-0.5 rounded-full text-xs cursor-pointer ${
                !selectedLevel3CategoryId && !selectedLevel2CategoryId
                  ? 'bg-blue-500 text-white shadow-sm'
                  : 'bg-gray-200 text-gray-700'
              }`}
              onClick={() => { setSelectedLevel3CategoryId(null); setSelectedLevel2CategoryId(null); setSelectedBrand(null); }}
            >
              Все
            </span>
            {selectedLevel3CategoryId && groupedCategories.get(selectedLevel3CategoryId) && (
              <span
                className="px-2 py-0.5 rounded-full text-xs bg-blue-500 text-white shadow-sm truncate"
                style={{maxWidth: 'calc(100% - 4rem)'}}
                title={groupedCategories.get(selectedLevel3CategoryId)?.level3Category.name || ''}
              >
                {groupedCategories.get(selectedLevel3CategoryId)?.level3Category.name || 'L3'}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-1 overflow-hidden">
            <span className="px-2 py-0.5 text-xs font-medium">Подкатегория:</span>
            {selectedLevel3CategoryId && (
              <span
                className={`px-2 py-0.5 rounded-full text-xs cursor-pointer ${
                  !selectedLevel2CategoryId
                    ? 'bg-blue-500 text-white shadow-sm'
                    : 'bg-gray-200 text-gray-700'
                }`}
                onClick={() => { setSelectedLevel2CategoryId(null); setSelectedBrand(null);}}
              >
                Все
              </span>
            )}
            {selectedLevel2CategoryId && selectedLevel3CategoryId && groupedCategories.get(selectedLevel3CategoryId)?.level2Categories.find(c => c.id === selectedLevel2CategoryId) && (
              <span
                className="px-2 py-0.5 rounded-full text-xs bg-green-500 text-white shadow-sm truncate"
                style={{maxWidth: 'calc(100% - 6rem)'}}
                title={groupedCategories.get(selectedLevel3CategoryId)?.level2Categories.find(c => c.id === selectedLevel2CategoryId)?.name || ''}
              >
                {groupedCategories.get(selectedLevel3CategoryId)?.level2Categories.find(c => c.id === selectedLevel2CategoryId)?.name || 'L2'}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-1 overflow-hidden">
            <span className="px-2 py-0.5 text-xs font-medium">Бренд:</span>
            <span
              className={`px-2 py-0.5 rounded-full text-xs cursor-pointer ${
                !selectedBrand
                  ? 'bg-blue-500 text-white shadow-sm'
                  : 'bg-gray-200 text-gray-700'
              }`}
              onClick={() => { handleBrandClick(null); setIsFilterPanelHovered(false); }}
            >
              Все
            </span>
            {selectedBrand && (
              <span
                className="px-2 py-0.5 rounded-full text-xs bg-purple-500 text-white shadow-sm truncate"
                style={{maxWidth: 'calc(100% - 6rem)'}}
                title={selectedBrand}
              >
                {selectedBrand}
              </span>
            )}
          </div>
        </div>

        {isFilterPanelHovered && (
          <div
            className="absolute top-0 left-0 right-0 bg-white bg-opacity-80 shadow-xl p-2 z-20 rounded-lg border border-gray-300"
          >
            <div className="grid grid-cols-3 gap-2">
              <div className="border border-gray-300 rounded-lg p-4 relative">
                <h3 className="absolute -top-3 left-3 bg-white px-1 text-sm font-semibold text-gray-600">Категория:</h3>
                <div className="flex flex-wrap gap-2 mt-2">
                  <span
                    className={`px-3 py-1 rounded-full text-sm cursor-pointer transition-colors duration-200 ${
                      !selectedLevel3CategoryId && !selectedLevel2CategoryId
                        ? 'bg-blue-500 text-white shadow'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300 hover:shadow-md'
                    }`}
                    onClick={() => { setSelectedLevel3CategoryId(null); setSelectedLevel2CategoryId(null); setSelectedBrand(null); setIsFilterPanelHovered(false); }}
                  >
                    Все
                  </span>
                  {Array.from(groupedCategories.values()).map(group => (
                    <span
                      key={group.level3Category.id}
                      className={`px-3 py-1 rounded-full text-sm cursor-pointer transition-colors duration-200 ${
                        selectedLevel3CategoryId === group.level3Category.id
                          ? 'bg-blue-500 text-white shadow'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300 hover:shadow-md'
                      }`}
                      onClick={() => { handleLevel3CategoryClick(group.level3Category.id); setIsFilterPanelHovered(false); }}
                    >
                      {group.level3Category.name || 'Без названия L3'}
                    </span>
                  ))}
                </div>
              </div>

              <div className="border border-gray-300 rounded-lg p-4 relative">
                <h3 className="absolute -top-3 left-3 bg-white px-1 text-sm font-semibold text-gray-600">Подкатегория:</h3>
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedLevel3CategoryId && ( 
                    <>
                      <span
                        className={`px-3 py-1 rounded-full text-sm cursor-pointer transition-colors duration-200 ${
                          selectedLevel3CategoryId && !selectedLevel2CategoryId
                            ? 'bg-blue-500 text-white shadow'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300 hover:shadow-md'
                        }`}
                        onClick={() => { setSelectedLevel2CategoryId(null); setSelectedBrand(null); setIsFilterPanelHovered(false); }}
                      >
                        Все
                      </span>
                      {groupedCategories.get(selectedLevel3CategoryId)?.level2Categories.map(category => (
                        <span
                          key={category.id}
                          className={`px-3 py-1 rounded-full text-sm cursor-pointer transition-colors duration-200 ${
                            selectedLevel2CategoryId === category.id
                              ? 'bg-green-500 text-white shadow'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300 hover:shadow-md'
                          }`}
                          onClick={() => { handleLevel2CategoryClick(category.id); setIsFilterPanelHovered(false); }}
                        >
                          {category.name || 'Без названия L2'}
                        </span>
                      ))}
                    </>
                  )}
                  {!selectedLevel3CategoryId && (
                     <p className="text-xs text-gray-500">Выберите категорию для отображения подкатегорий.</p>
                  )}
                </div>
              </div>

              <div className="border border-gray-300 rounded-lg p-4 relative">
                <h3 className="absolute -top-3 left-3 bg-white px-1 text-sm font-semibold text-gray-600">Бренд:</h3>
                <div className="flex flex-wrap gap-2 mt-2">
                  <span
                    className={`px-3 py-1 rounded-full text-sm cursor-pointer transition-colors duration-200 ${
                      !selectedBrand
                        ? 'bg-blue-500 text-white shadow'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300 hover:shadow-md'
                    }`}
                    onClick={() => { handleBrandClick(null); setIsFilterPanelHovered(false); }}
                  >
                    Все
                  </span>
                  {availableBrands.map(brand => (
                    <span
                      key={brand}
                      className={`px-3 py-1 rounded-full text-sm cursor-pointer transition-colors duration-200 ${
                        selectedBrand === brand
                          ? 'bg-purple-500 text-white shadow'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300 hover:shadow-md'
                      }`}
                      onClick={() => { handleBrandClick(brand); setIsFilterPanelHovered(false); }}
                    >
                      {brand}
                    </span>
                  ))}
                   {availableBrands.length === 0 && (selectedLevel3CategoryId || selectedLevel2CategoryId) && (
                    <p className="text-xs text-gray-500">Нет доступных брендов для выбранной категории.</p>
                  )}
                  {availableBrands.length === 0 && !selectedLevel3CategoryId && !selectedLevel2CategoryId && (
                    <p className="text-xs text-gray-500">Выберите категорию для отображения брендов.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col md:flex-row gap-2">
        <div className="w-full md:w-1/2">
          <div className="bg-white shadow-sm rounded-lg p-4 flex flex-col" style={{ maxHeight: '80vh' }}>
            <h2 className="text-md font-semibold mb-4">
              Искомые позиции:
              {application && application.products && application.products.length > 0 && (
                <span className="text-sm font-normal text-gray-600 ml-2">
                  {filteredSpns.length !== application.products.length
                    ? `(Показано ${filteredSpns.length} из ${application.products.length} позиций)`
                    : `(Всего ${application.products.length} позиций)`}
                </span>
              )}
            </h2>
            <div className="flex flex-col space-y-1 mb-4">
              {/* Прогресс-бар для SPN с заполненным article */}
              <div className="flex items-center">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400 w-24 flex-shrink-0">Есть артикул:</span>
                <div className="w-full bg-gray-200 rounded-full h-3 dark:bg-gray-700 mx-2">
                  <div
                    className="bg-green-500 h-3 rounded-full transition-all duration-300 ease-in-out"
                    style={{ width: `${articlesFilledPercentage}%` }}
                  ></div>
                </div>
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300 w-10 text-right flex-shrink-0">
                  {`${Math.round(articlesFilledPercentage)}%`}
                </span>
              </div>

              {/* Прогресс-бар для SPN с заполненными features (без article) */}
              <div className="flex items-center">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400 w-24 flex-shrink-0">Есть характеристики:</span>
                <div className="w-full bg-gray-200 rounded-full h-3 dark:bg-gray-700 mx-2">
                  <div
                    className="bg-yellow-500 h-3 rounded-full transition-all duration-300 ease-in-out"
                    style={{ width: `${featuresFilledPercentage}%` }}
                  ></div>
                </div>
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300 w-10 text-right flex-shrink-0">
                  {`${Math.round(featuresFilledPercentage)}%`}
                </span>
              </div>

              {/* Прогресс-бар для SPN без article и без features */}
              <div className="flex items-center">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400 w-24 flex-shrink-0">Нет данных:</span>
                <div className="w-full bg-gray-200 rounded-full h-3 dark:bg-gray-700 mx-2">
                  <div
                    className="bg-red-500 h-3 rounded-full transition-all duration-300 ease-in-out"
                    style={{ width: `${notFilledPercentage}%` }}
                  ></div>
                </div>
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300 w-10 text-right flex-shrink-0">
                  {`${Math.round(notFilledPercentage)}%`}
                </span>
              </div>
            </div>
            
            {application.products.length === 0 ? (
              <div className="bg-yellow-50 border border-yellow-100 text-yellow-800 p-4 rounded-lg">
                <p>В заявке нет искомых позиций.</p>
              </div>
            ) : (
              <div className="overflow-y-auto pr-2 flex-grow">
                {groupedBySection.hasSection ? (
                  // Отображение с группировкой по секциям
                  <>
                    {groupedBySection.groups.map(group => {
                      const isExpanded = expandedSections.has(group.section);
                      return (
                        <div key={group.section} className="mb-3">
                          {/* Заголовок секции */}
                          <div
                            className="bg-blue-50 border border-blue-200 rounded-lg p-3 cursor-pointer hover:bg-blue-100 transition-colors"
                            onClick={() => toggleSection(group.section)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <svg
                                  className={`w-5 h-5 text-blue-600 transition-transform ${isExpanded ? 'transform rotate-90' : ''}`}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                                <h3 className="font-semibold text-blue-900">{group.section}</h3>
                                <span className="text-sm text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">
                                  {group.count} {group.count === 1 ? 'позиция' : group.count < 5 ? 'позиции' : 'позиций'}
                                </span>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="text-sm font-medium text-blue-800">
                                  Сумма: {group.totalPrice.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽
                                </span>
                              </div>
                            </div>
                          </div>
                          
                          {/* Содержимое секции */}
                          {isExpanded && (
                            <div className="mt-2 space-y-1 pl-2">
                              {group.spns.map(spn => (
                                <SearchedProductCard
                                  key={spn.id}
                                  spn={spn}
                                  orderNumber={spn.orderNumber}
                                  onUpdate={handleSpnUpdate}
                                  onStartSearch={handleStartSearch}
                                  isSearching={searchingSpnIds.has(spn.id)}
                                  onViewVariants={handleViewVariants}
                                  isActive={selectedSpnId === spn.id}
                                  userId={userId === null ? 0 : userId}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    
                    {/* Несгруппированные элементы */}
                    {groupedBySection.ungrouped.length > 0 && (
                      <div className="mb-3">
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-2 mb-2">
                          <h3 className="font-semibold text-gray-700 text-sm">Прочие позиции</h3>
                        </div>
                        <div className="space-y-1">
                          {groupedBySection.ungrouped.map(spn => (
                            <SearchedProductCard
                              key={spn.id}
                              spn={spn}
                              orderNumber={spn.orderNumber}
                              onUpdate={handleSpnUpdate}
                              onStartSearch={handleStartSearch}
                              isSearching={searchingSpnIds.has(spn.id)}
                              onViewVariants={handleViewVariants}
                              isActive={selectedSpnId === spn.id}
                              userId={userId === null ? 0 : userId}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  // Старое отображение без группировки
                  <>
                    {(() => {
                      const sortedOriginalSpns = [...application.products].sort((a, b) => (a.orderNumber || 0) - (b.orderNumber || 0));
                      const filteredSpnIds = new Set(filteredSpns.map(fspn => fspn.id));
                      const elements: JSX.Element[] = [];
                      let lastEffectivelyRenderedOrderNumber = 0;
                      let i = 0;

                      while (i < sortedOriginalSpns.length) {
                        const currentOriginalSpn = sortedOriginalSpns[i];
                        const currentOrder = currentOriginalSpn.orderNumber;

                        if (currentOrder === undefined) {
                          // Элемент без orderNumber. Если он видим, отображаем его.
                          // Он не влияет на логику заглушек, основанную на orderNumber.
                          if (filteredSpnIds.has(currentOriginalSpn.id)) {
                            elements.push(
                              <SearchedProductCard
                                key={currentOriginalSpn.id}
                                spn={currentOriginalSpn}
                                orderNumber={undefined} // Явно указываем, что orderNumber отсутствует
                                onUpdate={handleSpnUpdate}
                                onStartSearch={handleStartSearch}
                                isSearching={searchingSpnIds.has(currentOriginalSpn.id)}
                                onViewVariants={handleViewVariants}
                                isActive={selectedSpnId === currentOriginalSpn.id}
                                userId={userId === null ? 0 : userId}
                              />
                            );
                          }
                          i++;
                          continue;
                        }

                        if (filteredSpnIds.has(currentOriginalSpn.id)) {
                          // Элемент видим и имеет orderNumber.
                          // Проверяем, был ли разрыв перед ним.
                          if (currentOrder > lastEffectivelyRenderedOrderNumber + 1) {
                            const skippedCount = currentOrder - (lastEffectivelyRenderedOrderNumber + 1);
                            // Эта заглушка для "дырки" между последним обработанным и текущим видимым.
                            elements.push(
                              <div key={`skipped-gap-${lastEffectivelyRenderedOrderNumber + 1}`} className="my-1 p-1 bg-gray-100 border border-gray-300 rounded-md text-center text-xs text-gray-600">
                                пропущено {skippedCount} позиций
                              </div>
                            );
                          }
                          // Отображаем видимый элемент.
                          elements.push(
                            <SearchedProductCard
                              key={currentOriginalSpn.id}
                              spn={currentOriginalSpn}
                              orderNumber={currentOrder}
                              onUpdate={handleSpnUpdate}
                              onStartSearch={handleStartSearch}
                              isSearching={searchingSpnIds.has(currentOriginalSpn.id)}
                              onViewVariants={handleViewVariants}
                              isActive={selectedSpnId === currentOriginalSpn.id}
                              userId={userId === null ? 0 : userId}
                            />
                          );
                          lastEffectivelyRenderedOrderNumber = currentOrder;
                          i++;
                        } else {
                          // Элемент не видим, но имеет orderNumber. Это начало или часть блока пропущенных.
                          const firstSkippedOrderInBlock = currentOrder;
                          let lastSkippedOrderInBlock = currentOrder;
                          let scanIndex = i;

                          // Ищем конец непрерывного блока невидимых элементов с orderNumber.
                          while (scanIndex < sortedOriginalSpns.length &&
                                 !filteredSpnIds.has(sortedOriginalSpns[scanIndex].id) &&
                                 sortedOriginalSpns[scanIndex].orderNumber !== undefined) {
                            lastSkippedOrderInBlock = sortedOriginalSpns[scanIndex].orderNumber!;
                            scanIndex++;
                          }
                          
                          // Количество пропущенных позиций в этом блоке.
                          // Блок начинается с firstSkippedOrderInBlock и заканчивается lastSkippedOrderInBlock.
                          // Все они идут после lastEffectivelyRenderedOrderNumber.
                          const skippedCountForThisBlock = lastSkippedOrderInBlock - lastEffectivelyRenderedOrderNumber;

                          if (skippedCountForThisBlock > 0) {
                            elements.push(
                              <div key={`skipped-block-${lastEffectivelyRenderedOrderNumber + 1}-${lastSkippedOrderInBlock}`} className="my-2 p-1 bg-gray-100 border border-gray-300 rounded-md text-center text-xs text-gray-600">
                                пропущено {skippedCountForThisBlock} позиций
                              </div>
                            );
                          }
                          lastEffectivelyRenderedOrderNumber = lastSkippedOrderInBlock;
                          i = scanIndex; // Перемещаем i на элемент после обработанного блока.
                        }
                      }
                      return elements;
                    })()}
                  </>
                )}
                {filteredSpns.length === 0 && application.products.length > 0 && (
                  <div className="text-gray-500 text-center py-4">
                    Нет позиций, соответствующих выбранным фильтрам.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="w-full md:w-1/2">
          {selectedSpn ? (
            <ProductVariantsList
              spn={selectedSpn}
              onProductSelected={handleSpnUpdate}
              onFeaturesSaved={handleFeaturesSaved} 
              refreshTrigger={variantsRefreshTrigger}
              selectedBrand={selectedBrand}
            />
          ) : (
            <div className="bg-gray-50 border border-gray-200 p-8 rounded-lg text-center">
              <p className="text-gray-500">
                Выберите позицию слева, чтобы просмотреть варианты продуктов
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Модальное окно переподбора брендов */}
      <BrandReselectionModal
        isOpen={isBrandReselectionModalOpen}
        onClose={() => setIsBrandReselectionModalOpen(false)}
        applicationId={parseInt(appId!)}
        level3Categories={getLevel3CategoriesForReselection()}
        onReselectionComplete={handleReselectionComplete}
      />
    </div>
  );
};

export default ApplicationPage;
